import { Box, CssBaseline, ThemeProvider } from '@mui/material'
import { useMemo, useState } from 'react'

import { useAppSelector } from '../../store/hooks'

import { GroupsStatusTabs, IGroupEntity } from '../../models/IGroup'

import GroupItem from 'components/groups/GroupItem'

import { useGetBarnList } from 'hook/query/useGetBarnList'
import { useGetGroups } from 'hook/query/useGetGroupsList'
import PageContainer from 'layouts/PageContainer'
import Preloader from 'ui/Preloader'
import { fontTheme } from '../../styles/font'
import { useGroupsAvailability } from './hooks/useGroupsAvailability'

import { GroupsFilters } from './components/GroupsFilters'

import { useGetTabsForGroups } from 'hook/query/useGetTabsForGroups'
import { GROUP_STATUS_TABS } from './constants/groupStatuses'

const theme = fontTheme

const GroupsPage = () => {
	const { permissions } = useAppSelector(state => state.authReducer)
	const [selectedStatuses, setSelectedStatuses] = useState<GroupsStatusTabs[]>(
		[],
	)

	const [currentBarns, setCurrentBarns] = useState<(string | number)[]>([])

	const handleActiveFilter = (barn: string | number) => {
		if (barn === 'all') {
			setCurrentBarns([])
			return
		}

		setCurrentBarns(prev =>
			prev.includes(barn) ? prev.filter(id => id !== barn) : [...prev, barn],
		)
	}

	const handleRemoveFilter = (barn: string | number) => {
		setCurrentBarns(prev => prev.filter(id => id !== barn))
	}

	const handleToggleStatus = (status: GroupsStatusTabs) => {
		setSelectedStatuses(prev =>
			prev.includes(status)
				? prev.filter(s => s !== status)
				: [...prev, status],
		)
	}

	const handleRemoveStatus = (status: GroupsStatusTabs) => {
		setSelectedStatuses(prev => prev.filter(s => s !== status))
	}

	const { barnList, loading: isLoadingBarnList } = useGetBarnList()

	const barnsQuery =
		currentBarns.length > 0 ? currentBarns.join(',') : undefined

	const { data: groupsList, isLoading } = useGetGroups({
		statuses: selectedStatuses,
		barns: barnsQuery,
	})

	const { data: groupsByBarns } = useGetGroups({
		barns: barnsQuery,
	})

	const { data: groupsByStatuses } = useGetGroups({
		statuses: selectedStatuses,
	})

	const hasFarmFoodTableLicense = Boolean(permissions?.license_food_table)
	const { data: foodTableGroups, isLoading: isLoadingFoodTableGroups } =
		useGetGroups({
			foodtableOnly: true,
			enabled: hasFarmFoodTableLicense,
		})
	const foodTableGroupIds = useMemo(
		() => new Set(foodTableGroups?.map(group => group.id) ?? []),
		[foodTableGroups],
	)

	const { availableStatuses, availableBarns } = useGroupsAvailability({
		groupsByBarns,
		groupsByStatuses,
		currentBarns,
	})

	const { data: tabsData } = useGetTabsForGroups()

	const visibleStatusTabs = useMemo(() => {
		if (!tabsData) return []

		return GROUP_STATUS_TABS.filter(tab => tabsData[tab.key] != null)
	}, [tabsData])

	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<PageContainer>
				<GroupsFilters
					selectedStatuses={selectedStatuses}
					onToggleStatus={handleToggleStatus}
					onRemoveStatus={handleRemoveStatus}
					availableStatuses={availableStatuses}
					barns={barnList ?? []}
					selectedBarns={currentBarns}
					onToggleBarn={handleActiveFilter}
					onRemoveBarn={handleRemoveFilter}
					availableBarns={availableBarns}
					showBarnsFilter={(barnList?.length ?? 0) > 1}
					statusTabs={visibleStatusTabs}
				/>

				{isLoading ||
				isLoadingBarnList ||
				(hasFarmFoodTableLicense && isLoadingFoodTableGroups) ? (
					<Preloader />
				) : (
					<>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: 'repeat(auto-fill, minmax(500px, 1fr))',
								alignItems: 'start',
								gap: '24px',
							}}
						>
							{groupsList
								?.filter(group => {
									// если коровники не выбраны — показываем всё
									if (currentBarns.length === 0) return true

									// если у группы нет barn — НЕ показываем
									if (group.barn === null || group.barn === undefined)
										return false

									return currentBarns.includes(group.barn)
								})
								.map((group: IGroupEntity) => (
									<GroupItem
										{...group}
										key={'group' + group.groupkey}
										hasFoodTableLicense={foodTableGroupIds.has(group.id)}
									/>
								))}
						</Box>
					</>
				)}
			</PageContainer>
		</ThemeProvider>
	)
}

export default GroupsPage
